// rootReducer.js
import { combineReducers } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import homeSlice from "./home/homeSlice";
import userSlice from "./user/userSlice";
import searchSlice from "./search/searchSlice";
import configSlice from "./config/configSlice";
import orderSlice from "./order/orderSlice";
import TENANT_ID from "./tenantId";

const createMemoryStorage = () => {
  const store = new Map();
  return {
    getItem(key) {
      return Promise.resolve(store.get(key) ?? null);
    },
    setItem(key, value) {
      store.set(key, value);
      return Promise.resolve(value);
    },
    removeItem(key) {
      store.delete(key);
      return Promise.resolve();
    },
  };
};

const createSafeSessionStorage = () => {
  if (typeof window === "undefined") return createMemoryStorage();
  try {
    const testKey = "__redux_persist_test__";
    window.sessionStorage.setItem(testKey, "1");
    window.sessionStorage.removeItem(testKey);
    return createWebStorage("session");
  } catch {
    return createMemoryStorage();
  }
};

// TTL storage wrapper: adds a timestamp to the entire serialized value.
// On read, returns null (triggers initial-state fallback) if older than ttlMs.
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

const createTtlStorage = (baseStorage, ttlMs = SESSION_TTL_MS) => ({
  getItem(key) {
    return baseStorage.getItem(key).then((raw) => {
      if (!raw) return null;
      try {
        const wrapper = JSON.parse(raw);
        if (
          wrapper &&
          wrapper._ttl &&
          Date.now() - wrapper._ttl > ttlMs
        ) {
          // Expired — remove and return null so redux-persist uses initial state
          baseStorage.removeItem(key);
          return null;
        }
        // Return the original value (the JSON string redux-persist expects)
        return wrapper._v ?? null;
      } catch {
        // Not wrapped (legacy data) — discard it
        baseStorage.removeItem(key);
        return null;
      }
    });
  },
  setItem(key, value) {
    // Wrap the serialized value with a timestamp
    const wrapped = JSON.stringify({ _ttl: Date.now(), _v: value });
    return baseStorage.setItem(key, wrapped);
  },
  removeItem(key) {
    return baseStorage.removeItem(key);
  },
});

const sessionStorage = createTtlStorage(createSafeSessionStorage());

const searchPersistConfig = {
  key: `search:${TENANT_ID}`,
  storage: sessionStorage,
};

const orderPersistConfig = {
  key: `order:${TENANT_ID}`,
  storage: sessionStorage,
};

const homePersistConfig = {
  key: `home:${TENANT_ID}`,
  storage: sessionStorage,
};

const rootReducer = combineReducers({
  home: persistReducer(homePersistConfig, homeSlice),
  user: userSlice,
  search: persistReducer(searchPersistConfig, searchSlice),
  config: configSlice,
  order: persistReducer(orderPersistConfig, orderSlice),
});

export { rootReducer };
